/**
 *  React implementation of the the SbHtml 'Multi-Select' Widget
 * 
 *  A widget that allows a user to move items between an available 'remaining' list, and a 'selected' list
 *  by highlighting items and clicking either the 'Add >>' or '<< Remove' buttons
 * 
 *  Required React Properties
 * 
 *    - name: When posting a form containing this widget to the server, the selected values will be
 *            indexed in $_POST by this value.
 *            
 *    - keyPrefix: All options in react are required to have a unique key. This value is used to generate such a key.
 *    
 *    - selectedItems: A list of the selected items
 *    
 *    - allItems: A list of all the items (list of remaining items can be inferred from allItems - selectedItems)
 *    
 *    - addOptionsHandler: Fires when the 'Add >>' button is clicked
 *    
 *    - removeOptionsHandler: Fires when the '<< Remove' button is clicked
 *    
 *    - remainingHighlightHandler: Fires when an item(s) is highlighted/un-highlighted in the remaining items list
 *    
 *    - selectedHighlightHandler: Fires when an item(s) is highlighted/un-highlighted in the selected items list
 *    
 *    - remainingHighlightedItems: The highlighted values for the remaining items list
 *    
 *    - selectedHighlightedItems: The highlighted values for the selected items list
 */

import * as React from "react"
import * as Utils from "../utils"
import * as Immutable from "immutable"
import {HTMLOptionLikeElement} from "../types";

export type Context = Immutable.OrderedMap<string,string>
export type ContextFormat = {[key:string]:string}

export interface MultiSelectProps {
	name:string
	keyPrefix:string
	selectedItems:Context
	allItems:Context

	addOptionsHandler:() => void
	removeOptionsHandler:() => void

	remainingHighlightHandler:(_:Context) => void
	selectedHighlightHandler:(_:Context) => void

	remainingHighlightedItems:Context
	selectedHighlightedItems:Context
}

export class MultiSelect extends React.Component<MultiSelectProps,{}> {
	render():JSX.Element {
		const forwardLabel  = "Add >>";
		const backwardLabel = "<< Remove";

		const remainingItems = Utils.mapSubtract(this.props.allItems,this.props.selectedItems)
		const selectedOptions = this.selectedItemsFromOptions(this.props.selectedItems)
		const remainingOptions = this.selectedItemsFromOptions(remainingItems)

		/**
		 * On given an FormEvent, this function loops over the options of the HtmlSelectElement that generated
		 * the event.
		 * For each option it checks if it is highlighted.
		 * If it is, it is pushes its id (the option's value) into an array of selected ids. 
		 * This list is then used to return the corresponding set of items from the "all" available items map.
		 */
		const findHighlightedItems = (e:React.FormEvent<HTMLSelectElement>) => {
			const el: HTMLSelectElement = e.currentTarget
			let selectedIds:string[] = []
			for (let i = 0, l = el.options.length; i < l; i++) {
				const option = el.options[i] as HTMLOptionLikeElement
				if (option.selected) {
					selectedIds.push(option.value)
				}
			}
			return this.props.allItems.filter((v,k) => selectedIds.find((optionsValue) => k === optionsValue) !== undefined ) as Context
		}
		
		const remainingHighlightHandler = (e:React.FormEvent<HTMLSelectElement>) => {
			this.props.remainingHighlightHandler(findHighlightedItems(e))
		}

		const selectedHighlightHandler = (e:React.FormEvent<HTMLSelectElement>) => {
			this.props.selectedHighlightHandler(findHighlightedItems(e))
		}
		
		// Replace this with some kind javascript join once Immutable typings are not rubbish
		const valueString = this.props.selectedItems.reduce(
			(p?:string,v?:string,k?:string) => p === undefined || p === '' ? k : p + ',' + k,
			''
		)

		return <div style={{width:'100%'}}>
			<table className="sbHbox" style={{width:"100%", height:"100%"}} cellSpacing="0" cellPadding="5">
				<tbody>
				<tr>
					<td style={{width:"50%"}} className="hboxElement">
						<button onClick={this.props.addOptionsHandler} type="button" className="sbButton sbRoundedEdges" style={{width:'100%'}}>{forwardLabel}</button>
						<br/>
						<select onChange={remainingHighlightHandler} value={Utils.mapKeys(this.props.remainingHighlightedItems)} className="sbSelect" style={{width: '100%'}} size={10} multiple={true}>
							{remainingOptions}
							</select>
					</td>
					<td style={{width:"50%"}} className="hboxElement">
						<button onClick={this.props.removeOptionsHandler} type="button" className="sbButton sbRoundedEdges" style={{width:'100%'}}>{backwardLabel}</button>
						<br/>
						<input name={this.props.name} type="hidden" value={valueString}/>
						<select onChange={selectedHighlightHandler} value={Utils.mapKeys(this.props.selectedHighlightedItems)} className="sbSelect" style={{width:'100%'}} size={10} multiple={true}>
							{selectedOptions}
							</select>
					</td>
					</tr>
				</tbody>
				</table>
		</div>
	}

	selectedItemsFromOptions(options:Context) {
		return options.map(
			// No idea why v and k include undefined. They don't with the ImmutableJS 4.0 types.
			(v, k) => this.createOptionElement(v!, k!)
		).toArray()
	}

	createOptionElement(v:string,k:string) {
		return <option key={this.props.keyPrefix + '-' + k} value={k}>{v}</option>
	}
}



// WEBPACK FOOTER //
// ./src/lib/sbhtml/multi-select.tsx